import { Component, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { IssuesService, IssueSearchSpec, OrderParameters } from "../../../common/services/issues.service";
//import { GridOptions, ColDef, GridApi, ColumnApi, RedrawRowsParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Issue, IssueType, IssuesStatus } from "../../../common/models/issue.model";
import { TabComponent } from "../../../common/components/tabs/tab.component";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { WellService } from "../../../common/services/well.service";
import { WellStub } from "../../../common/models/well.model";
import { AuthService } from "../../../services/auth.service";
import { OrganizationService } from "../../../common/services/organization.service";
import { OrganizationStub } from "../../../common/models/organization.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { LoaderService } from "../../../common/services/loader.service";

@Component({
    selector: 'view-my-issues',
    templateUrl: './view-my-issues.component.html',
    styleUrls: ['./view-my-issues.component.scss'],
    standalone: false
})
export class ViewMyIssuesComponent implements OnInit {

    subheader: SubHeader;
    selectedTabName: string = "Open";

    openData: Observable<Issue[]>;
    closedData: Observable<Issue[]>;

    issueTypes: IssueType[];


    organization: OrganizationStub;
    ownedWells: WellStub[];

    openGridOptions: GridOptions;
    openGridApi?: GridApi;
    closedGridOptions: GridOptions;
    closedGridApi?: GridApi;

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }

    apiColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "API #",
        headerTooltip: "API #",
        field: "wellId",
        valueGetter: this.formatter.formatPotentiallyBlankField("wellId", ""),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellId", ""),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
    } as ColDef;

    closedDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Closed",
        headerTooltip: "Date Closed",
        field: "resolutionDate",
        tooltipValueGetter: this.formatter.formatDate("resolutionDate"),
        valueGetter: this.formatter.formatDate("resolutionDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145
    } as ColDef;

    createdDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Created",
        headerTooltip: "Date Created",
        field: "creationDate",
        tooltipValueGetter: this.formatter.formatDate("creationDate"),
        valueGetter: this.formatter.formatDate("creationDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145
    } as ColDef;

    descriptionColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Description",
        headerTooltip: "Issue Description",
        field: "description",
        tooltipField: "description",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 225
    } as ColDef;

    issueNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Number",
        headerTooltip: "Issue Number",
        field: "identifier",
        tooltipField: "identifier",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 120
    } as ColDef;

    issueTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Code",
        headerTooltip: "Issue Code",
        field: "typeId",
        tooltipValueGetter: this.formatIssueType("typeId"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 120,
    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.formatWellName("wellId"),
        tooltipValueGetter: this.formatWellName("wellId"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 100
    } as ColDef;

    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Number",
        headerTooltip: "Well Number",
        valueGetter: this.formatWellNumber("wellId"),
        tooltipValueGetter: this.formatWellNumber("wellId"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 100
    } as ColDef;



    constructor(private formatter: GridFormatterService, private issuesService: IssuesService, private wellService: WellService, private auth: AuthService, private subHeaderService: SubHeaderService, private loader: LoaderService,
        private organizationService: OrganizationService) { }

    ngOnInit() {
        this.subheader = new SubHeader({ title: "My Issues", buttons: [{ name: "Export Current Tab", method: () => this.exportTab() }] });
        this.subHeaderService.updateSubHeader(this.subheader);

        this.defineGridOptions();
        this.loadOpenIssues();
    }

    defineGridOptions(): void {
        const openColumns: ColDef[] = [];
        const closedColumns: ColDef[] = [];

        openColumns.push(this.createdDateColumn);
        openColumns.push(this.issueNumberColumn);
        openColumns.push(this.descriptionColumn);
        openColumns.push(this.apiColumn);
        openColumns.push(this.wellNameColumn);
        openColumns.push(this.wellNumberColumn);

        closedColumns.push(this.createdDateColumn);
        closedColumns.push(this.closedDateColumn);
        closedColumns.push(this.issueNumberColumn);
        closedColumns.push(this.descriptionColumn);
        closedColumns.push(this.apiColumn);
        closedColumns.push(this.wellNameColumn);
        closedColumns.push(this.wellNumberColumn);

        this.openGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(openColumns, false);
        this.openGridOptions.onGridReady = (params) => {
            this.openGridApi = params.api;
        };
        this.closedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(closedColumns, false);
        this.closedGridOptions.onGridReady = (params) => {
            this.closedGridApi = params.api;
        };
    }

    exportTab(): void {
        this.loader.toggleLoader(true);
        let api: GridApi | undefined;
        let now = new Date();
        let fileName: string = "";
        switch (this.selectedTabName) {
            case "Open":
                api = this.openGridApi;
                fileName = `Open_Issues_${now.toLocaleString()}`;
                break;
            case "Closed":
                api = this.closedGridApi;
                fileName = `Closed_Issues_${now.toLocaleString()}`;
        }
        setTimeout(() => { this.loader.toggleLoader(false); }, 5000);
        this.formatter.exportDataToExcel(fileName, "Issues", api, false);
    }

    formatIssueType(field: string): any {
        return (obj: any) => {
            let returnValue = "N/A";
            if (obj.data && obj.data[field] != undefined && obj.data[field] != null) {
                let matchingIssueTypes = this.issueTypes.filter(a => a.id == obj.data[field]);
                if (matchingIssueTypes && matchingIssueTypes.length > 0)
                    returnValue = `(${matchingIssueTypes[0].id}) ${matchingIssueTypes[0].description}`;
            }

            return returnValue;
        }
    }

    formatWellName(field: string): any {
        return (obj: any) => {
            let returnValue = "";
            if (obj && obj.data && obj.data[field] != null && this.ownedWells != null) {
                const well = this.ownedWells.find(w => w.id === obj.data[field].toString());
                if (well != null) {
                    returnValue = (well.lease != null) ? well.lease.name : well.name;
                }
            }
            return returnValue;
        }
    }

    formatWellNumber(field: string): any {
        return (obj: any) => {
            let returnValue = "";
            if (obj && obj.data && obj.data[field] != null && this.ownedWells != null) {
                const well = this.ownedWells.find(w => w.id === obj.data[field].toString());
                if (well != null) {
                    returnValue = well.wellNumber;
                }
            }
            return returnValue;
        }
    }

    loadClosedIssues(): void {
        const typeObs$: Observable<IssueType[]> = (this.issueTypes == null || this.issueTypes.length === 0)
            ? this.issuesService.getAllIssueTypes()
            : of(this.issueTypes);

        const organization$: Observable<OrganizationStub> = (this.organization == null)
            ? this.organizationService.getOrganizationStub(this.auth.User.organizationId)
            : of(this.organization);

        const spec = {
            organizationIds: [],
            loadChildren: false,
            statuses: [IssuesStatus.Closed],
            ordering: {
                ascending: false,
                field: "creationDate"
            } as OrderParameters,
            issueIsResolved: true,
            includeIssuesByWellOwnership: true
        } as IssueSearchSpec;

        this.closedData = typeObs$.pipe(
            switchMap((types: IssueType[]) => {
                this.issueTypes = types;
                return organization$;
            }),
            switchMap((organization: OrganizationStub) => {
                this.organization = organization;
                if (this.ownedWells != null && this.ownedWells.length > 0) return of(this.ownedWells);
                const orgs: string[] = [];
                orgs.push(this.organization.id);
                if (this.organization.historicalOwnerNumber != null && this.organization.historicalOwnerNumber.length > 0) orgs.push(this.organization.historicalOwnerNumber);
                return this.wellService.getCurrentAndHistoricalWellStubs(orgs);
            }),
            switchMap((wells: WellStub[]) => {
                this.ownedWells = wells;
                spec.organizationIds = [this.organization.id];
                spec.wellIds = wells.map(m => m.id);
                if (this.organization.historicalOwnerNumber != null && this.organization.historicalOwnerNumber.length > 0) spec.legacyIds = [this.organization.historicalOwnerNumber];
                return this.issuesService.getIssues(spec);
            })
        );
    }

    loadOpenIssues(): void {
        const typeObs$: Observable<IssueType[]> = (this.issueTypes == null || this.issueTypes.length === 0)
            ? this.issuesService.getAllIssueTypes()
            : of(this.issueTypes);

        const organization$: Observable<OrganizationStub> = (this.organization == null)
            ? this.organizationService.getOrganizationStub(this.auth.User.organizationId)
            : of(this.organization);

        const spec = {
            organizationIds: [],
            loadChildren: false,
            statuses: [IssuesStatus.Validated, IssuesStatus.Escalated, IssuesStatus.ChiefsOrderApproval, IssuesStatus.Contested],
            ordering: {
                ascending: false,
                field: "creationDate"
            } as OrderParameters,
            includeIssuesByWellOwnership: true
        } as IssueSearchSpec;

        this.openData = typeObs$.pipe(
            switchMap((types: IssueType[]) => {
                this.issueTypes = types;
                return organization$;
            }),
            switchMap((organization: OrganizationStub) => {
                this.organization = organization;
                if (this.ownedWells != null && this.ownedWells.length > 0) return of(this.ownedWells);
                const orgs: string[] = [];
                orgs.push(this.organization.id);
                if (this.organization.historicalOwnerNumber != null && this.organization.historicalOwnerNumber.length > 0) orgs.push(this.organization.historicalOwnerNumber);
                return this.wellService.getCurrentAndHistoricalWellStubs(orgs);
            }),
            switchMap((wells: WellStub[]) => {
                this.ownedWells = wells;
                spec.organizationIds = [this.organization.id];
                if (this.organization.historicalOwnerNumber != null && this.organization.historicalOwnerNumber.length > 0) spec.legacyIds = [this.organization.historicalOwnerNumber];
                spec.wellIds = wells.map(m => m.id);
                return this.issuesService.getIssues(spec);
            })
        );

    }

    onTabClicked(component: TabComponent): void {
        this.selectedTabName = component.title;
        let api: GridApi | null = null;
        switch (this.selectedTabName) {
            case "Open":                
                this.loadOpenIssues();
                if (this.openGridOptions != null) api = this.openGridApi;
                break;
            case "Closed":
                this.loadClosedIssues();
                if (this.closedGridOptions) api = this.closedGridApi;
                break;
        }
        if (api != null) api.sizeColumnsToFit();
    }

}
